import React, { useState, useEffect } from "react";
import { Heart, Users, Target } from "lucide-react";
import { contentAPI } from "../services/api";
import { useLanguage } from "../hooks/useLanguage";

const AboutSection = () => {
  const currentLanguage = useLanguage();
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const sections = await contentAPI.getSections("home").catch(() => []);
        const aboutSection = sections.find(s => s.section_type === "about");
        if (aboutSection?.content_data) {
          setSectionData(aboutSection.content_data);
        }
      } catch (error) {
        console.error("Error fetching about section:", error);
      }
    };
    fetchSectionData();
  }, []);

  const getStringValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      return value[currentLanguage] || value['de'] || value[Object.keys(value)[0]] || fallback;
    }
    return String(value) || fallback;
  };

  const sectionTitle = sectionData ? getStringValue(sectionData.title, "Über uns") : "Über uns";
  const sectionDescription = sectionData ? getStringValue(sectionData.description, "Professionelle Pflege und Betreuung mit Herz und Kompetenz") : "Professionelle Pflege und Betreuung mit Herz und Kompetenz";
  const missionTitle = sectionData ? getStringValue(sectionData.mission_title, "Unsere Zielsetzung") : "Unsere Zielsetzung";
  const missionDescription = sectionData ? getStringValue(sectionData.mission_description, "Wir fördern und erhalten die Fähigkeit der Menschen, die sich an uns wenden, um ihr Leben so unabhängig wie möglich führen und gestalten zu können.") : "Wir fördern und erhalten die Fähigkeit der Menschen, die sich an uns wenden, um ihr Leben so unabhängig wie möglich führen und gestalten zu können.";
  const approachTitle = sectionData ? getStringValue(sectionData.approach_title, "Unser Ansatz") : "Unser Ansatz";
  const approachDescription = sectionData ? getStringValue(sectionData.approach_description, "Durch den Aufbau einer vertrauten Beziehung und gegenseitige Anerkennung versuchen wir unseren Patienten eine Unterstützung in ihren Lebensaktivitäten zu geben.") : "Durch den Aufbau einer vertrauten Beziehung und gegenseitige Anerkennung versuchen wir unseren Patienten eine Unterstützung in ihren Lebensaktivitäten zu geben.";
  const valuesTitle = sectionData ? getStringValue(sectionData.values_title, "Unsere Werte") : "Unsere Werte";
  const valuesDescription = sectionData ? getStringValue(sectionData.values_description, "Zu unserem humanistischen Grundverständnis gehört, jeden Menschen in seiner Ganzheit und Einzigartigkeit wahrzunehmen und ihm mit Toleranz und Achtung zu begegnen.") : "Zu unserem humanistischen Grundverständnis gehört, jeden Menschen in seiner Ganzheit und Einzigartigkeit wahrzunehmen und ihm mit Toleranz und Achtung zu begegnen.";
  const mainDescription = sectionData ? getStringValue(sectionData.main_description, "Als ambulanter Pflegedienst bieten wir unsere Dienstleistung allen Menschen an, die Beratung, Pflege und Hilfe im Bereich der häuslichen Alten-, Kranken- und Behindertenpflege in Anspruch nehmen möchten.") : "Als ambulanter Pflegedienst bieten wir unsere Dienstleistung allen Menschen an, die Beratung, Pflege und Hilfe im Bereich der häuslichen Alten-, Kranken- und Behindertenpflege in Anspruch nehmen möchten.";

  return (
    <section id="about" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-[var(--vortex-surface)]">
      <div className="max-w-7xl mx-auto w-full min-w-0">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-[var(--vortex-text-primary)]">
            {sectionTitle}
          </h2>
          <p className="text-base sm:text-xl text-[var(--vortex-text-secondary)] max-w-3xl mx-auto leading-relaxed">
            {sectionDescription}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-10 sm:mb-16">
          {/* Mission */}
          <div className="bg-[var(--vortex-surface-alt)] p-4 sm:p-6 md:p-8 rounded-xl border-2 border-[var(--vortex-border)] hover:border-[var(--vortex-accent-primary)] transition-colors">
            <div className="w-16 h-16 rounded-full bg-[var(--vortex-accent-primary)]/10 flex items-center justify-center mb-6">
              <Target className="w-8 h-8 text-[var(--vortex-accent-primary)]" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-[var(--vortex-text-primary)]">
              {missionTitle}
            </h3>
            <p className="text-lg text-[var(--vortex-text-secondary)] leading-relaxed">
              {missionDescription}
            </p>
          </div>

          {/* Approach */}
          <div className="bg-[var(--vortex-surface-alt)] p-4 sm:p-6 md:p-8 rounded-xl border-2 border-[var(--vortex-border)] hover:border-[var(--vortex-accent-primary)] transition-colors">
            <div className="w-16 h-16 rounded-full bg-[var(--vortex-health)]/10 flex items-center justify-center mb-6">
              <Heart className="w-8 h-8 text-[var(--vortex-health)]" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-[var(--vortex-text-primary)]">
              {approachTitle}
            </h3>
            <p className="text-lg text-[var(--vortex-text-secondary)] leading-relaxed">
              {approachDescription}
            </p>
          </div>

          {/* Values */}
          <div className="bg-[var(--vortex-surface-alt)] p-4 sm:p-6 md:p-8 rounded-xl border-2 border-[var(--vortex-border)] hover:border-[var(--vortex-accent-primary)] transition-colors">
            <div className="w-16 h-16 rounded-full bg-[var(--vortex-accent-primary)]/10 flex items-center justify-center mb-6">
              <Users className="w-8 h-8 text-[var(--vortex-accent-primary)]" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-[var(--vortex-text-primary)]">
              {valuesTitle}
            </h3>
            <p className="text-lg text-[var(--vortex-text-secondary)] leading-relaxed">
              {valuesDescription}
            </p>
          </div>
        </div>

        {/* Main Description */}
        <div className="bg-[var(--vortex-surface-alt)] p-4 sm:p-6 md:p-10 rounded-xl border-2 border-[var(--vortex-border)] mb-8 sm:mb-12">
          <p className="text-base sm:text-xl text-[var(--vortex-text-primary)] leading-relaxed text-center max-w-4xl mx-auto">
            {mainDescription}
          </p>
        </div>

        {/* Image Gallery - Caregivers with Elderly */}
        {sectionData?.images && sectionData.images.length > 0 ? (
          <div className={`grid gap-4 sm:gap-6 ${sectionData.images.length === 1 ? 'md:grid-cols-1' : 'md:grid-cols-2'}`}>
            {sectionData.images.map((image, index) => (
              <div key={index} className="relative w-full rounded-xl overflow-hidden border-2 border-[var(--vortex-border)] shadow-lg bg-[var(--vortex-surface)] flex items-center justify-center">
                {image.url ? (
                  <img src={image.url} alt={image.alt || `About image ${index + 1}`} className="w-full h-auto object-contain max-h-[600px] sm:max-h-[700px]" />
                ) : (
                  <div className="w-full min-h-64 sm:min-h-80 bg-gradient-to-br from-[var(--vortex-accent-primary)]/15 to-[var(--vortex-health)]/15 flex items-center justify-center">
                    <div className="text-center p-6">
                      <Users className="w-12 h-12 text-[var(--vortex-accent-primary)] mx-auto mb-3" />
                      <p className="text-base text-[var(--vortex-text-primary)] font-medium">
                        Bildplatzhalter
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            <div className="relative w-full min-h-64 sm:min-h-80 rounded-xl overflow-hidden border-2 border-[var(--vortex-border)] shadow-lg flex items-center justify-center">
              <div className="w-full min-h-64 sm:min-h-80 bg-gradient-to-br from-[var(--vortex-accent-primary)]/15 to-[var(--vortex-health)]/15 flex items-center justify-center">
                <div className="text-center p-6">
                  <Users className="w-12 h-12 text-[var(--vortex-accent-primary)] mx-auto mb-3" />
                  <p className="text-base text-[var(--vortex-text-primary)] font-medium">
                    Bildplatzhalter: Pflegekraft mit Senior
                  </p>
                </div>
              </div>
            </div>
            <div className="relative w-full min-h-64 sm:min-h-80 rounded-xl overflow-hidden border-2 border-[var(--vortex-border)] shadow-lg flex items-center justify-center">
              <div className="w-full min-h-64 sm:min-h-80 bg-gradient-to-br from-[var(--vortex-health)]/15 to-[var(--vortex-accent-primary)]/15 flex items-center justify-center">
                <div className="text-center p-6">
                  <Heart className="w-12 h-12 text-[var(--vortex-health)] mx-auto mb-3" />
                  <p className="text-base text-[var(--vortex-text-primary)] font-medium">
                    Bildplatzhalter: Glückliche Senioren
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AboutSection;

